import { NgClass, NgTemplateOutlet } from '@angular/common';
import { HttpErrorResponse, HttpStatusCode } from '@angular/common/http';
import { Component, DestroyRef, OnInit } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { MatIconRegistry } from '@angular/material/icon';
import { DomSanitizer } from '@angular/platform-browser';
import { BROWSER_LOCALE_SENTINEL, SMART_WIDGET_ERROR_CODES, WIDGET_TYPE } from '@constants/app.constants';
import { MAT_ICON_LITERALS } from '@constants/mat-icon-literals.constants';
import { WINDOW_LISTENER_CONSTANTS } from '@constants/window-listener.constants';
import { environment } from '@environments/environment';
import { BookingComponent } from '@features/booking/booking.component';
import { LeadGeneratorComponent } from '@features/lead-generator/lead-generator.component';
import { SurveyComponent } from '@features/survey/survey.component';
import { CalendarAppointmentModel } from '@models/appointment-booking.model';
import { CUSTOM_CSS_WIDGET_TYPE, CssOverwrites } from '@models/css-overwrites.model';
import { WidgetConfModel } from '@models/widget-conf.model';
import { WorkerDbModel } from '@models/worker-db.model';
import { TranslateModule, TranslateService } from '@ngx-translate/core';
import { AppointmentService } from '@services/feature/appointment.service';
import { BookingService } from '@services/feature/booking.service';
import { CssOverwriteService } from '@services/feature/css-overwrite.service';
import { CustomEventService } from '@services/feature/custom-event.service';
import { WidgetService } from '@services/feature/widget.service';
import { DomManipulationService } from '@services/utils/dom-manipulation.service';
import { HelperService } from '@services/utils/helper.service';
import { LoggerService } from '@services/utils/logger.service';
import { SwRouteService } from '@services/utils/sw-route.service';
import { AlertComponent } from '@ui-lib/alert/alert.component';
import { WidgetLayoutComponent } from '@ui-lib/layout/widget-layout/widget-layout.component';
import { Observable, finalize, forkJoin } from 'rxjs';

@Component({
  selector: 'app-root',
  templateUrl: './app.component.html',
  styleUrls: ['./app.component.scss'],
  imports: [SurveyComponent, BookingComponent, LeadGeneratorComponent, AlertComponent, TranslateModule, WidgetLayoutComponent, NgClass, NgTemplateOutlet]
})
export class AppComponent implements OnInit {
  readonly customCssWidgetType = CUSTOM_CSS_WIDGET_TYPE;
  readonly brandName = (environment as any).brandName || 'Calenso';

  // Asset base for absolute URLs: same-origin for the iframe, ours (not the integrator's) for the WC.
  readonly deployUrl = environment.deployUrl;

  showPopup = false;
  widgetTypes = WIDGET_TYPE;
  queryStringAppointmentUuid: string;
  localStorageaccessible = true;
  iframeResizerLoaded = false;
  isSmartWidgetLoading = true;
  isPartnerBlocked = false; // Partner is blocked or deleted — the configuration call is refused with 403 partner_blocked
  isBookingWidgetDisabled = false; // Partner's booking widget is switched off — the configuration call is refused with 403 booking_widget_disabled (JB-1637)
  hasBootstrapError = false; // The configuration call failed for an unrecognized reason (unknown 403 code, 5xx, network) — show the unavailable card instead of a blank widget

  constructor(
    public widgetService: WidgetService,
    private translate: TranslateService,
    private customEventService: CustomEventService,
    private swRouteService: SwRouteService,
    private appointmentService: AppointmentService,
    private cssOverwriteService: CssOverwriteService,
    private domManipulationService: DomManipulationService,
    private helperService: HelperService,
    private iconRegistry: MatIconRegistry,
    private sanitizer: DomSanitizer,
    private bookingService: BookingService,
    private destroyRef: DestroyRef
  ) {
    this.addSvgIcon();
    this.customEventService.setWidgetConfigurationFromQueryParamsEvent
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe(result => {
        this.setWidgetConfigurationFromQueryParams(
          result.partner_uuid,
          result.widget_uuid
        );
      });

    this.customEventService.cssOverwriteTemplateUuid
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe({
        next: (template: { templateUuid: string }) => {
          const cssOverwritesObservable: Observable<CssOverwrites>[] = [];
          if (template?.templateUuid) {
            cssOverwritesObservable.push(this.cssOverwriteService.getCssOverwriteUsingUuid(template.templateUuid));
          } else {
            cssOverwritesObservable.push(this.cssOverwriteService.getCssOverwrites());
          }

          forkJoin(cssOverwritesObservable).subscribe({
            next: (cssOverwrite) => {
              if (cssOverwrite[0] === null) {
                LoggerService.warn('Invalid css-overwrite', template.templateUuid);
              } else if (this.widgetService.widgetConf.partner_id !== cssOverwrite[0].partner_id) {
                LoggerService.warn('Partner id is not matching for apply pre-define css-overwrite');
              } else if (cssOverwrite[0].type !== this.customCssWidgetType.smart_widget) {
                LoggerService.error('Invalid css-overwrite type');
              } else if (cssOverwrite[0].smart_widget_configuration_id !== this.widgetService.widgetConf.id) {
                if (template?.templateUuid) {
                  LoggerService.warn(`Requested template(${template.templateUuid}) is not configured for this widget(${this.widgetService.widgetConf.widget_uuid})`);
                } else {
                  LoggerService.log('[css-overwrite]', `No CSS is active for this widget(${this.widgetService.widgetConf.widget_uuid})`);
                }
              } else {
                if (template?.templateUuid) {
                  LoggerService.log('[css-overwrite]', 'applying css-overwrite provided in url parameter', cssOverwrite[0].uuid);
                } else {
                  LoggerService.log('[css-overwrite]', 'applying activated css-overwrite manually', cssOverwrite[0].uuid);
                }
                this.domManipulationService.applyCustomCss(cssOverwrite[0]);
              }
            },
            error: () => {
              // Silently ignore errors
            }
          });
        }
      });
  }

  toggleWidget(): void {
    LoggerService.log('Inside toggleWidget()');
  }

  togglePopup(): void {
    this.showPopup = !this.showPopup;
  }

  ngOnInit(): void {
    // The widget never owns the document — it always renders inside someone else's page, and
    // on an integrator page a runtime title/description write silently replaces the host's tab
    // name and its SEO description. Our own index pages carry both as static tags instead.

    // Show localstorage not accessable warning
    this.localStorageaccessible = this.helperService.storageAvailable('localStorage');

    this.customEventService.resetMainWidgetEvent
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe(() => {
        this.widgetService.widgetConf = undefined;
        this.getWidgetConfiguration(this.widgetService.partnerUUID, this.widgetService.widgetUUID);
      });

    this.customEventService.queryStringAppointmentUuid
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe({
        next: appointmentUuid => this.getAppointment(appointmentUuid),
      });

    window.addEventListener('message', (event) => {
      // WC mode has no parent frame — a host-page message must not drive the widget.
      if (this.widgetService.isWebComponentMode) {
        return;
      }
      if (event?.data) {
        if (event.data.eventName === WINDOW_LISTENER_CONSTANTS.PARENT_TO_CHILD_WEBSITE_EVENT) {
          LoggerService.log('before setting parentToChildOriginEvent');
          this.widgetService.parentToChildOriginEvent = event;
        }

        if (event.data.eventName === WINDOW_LISTENER_CONSTANTS.BOOT) {
          if (event.data.widgetConf) {
            this.widgetService.requestedClientDomain = event.data.clientDomain;
            this.widgetService.clientOriginEvent = event;
            this.widgetService.layoutType = event.data.widgetConf.layoutType;
            LoggerService.log('this.requestedClientDomain ', this.widgetService.requestedClientDomain);

            if (event.data.widgetConf.customerConfData) {
              // create customer data
              this.swRouteService.setCustomerDataFromIframePostMessage(event.data.widgetConf.customerConfData);
            }
            if (event.data.widgetConf.customFieldValues) {
              // create custom field values data
              this.swRouteService.setCustomFieldValuesFromIframePostMessage(event.data.widgetConf.customFieldValues);
            }
            // set reference appointment uuid
            if (
              event.data.widgetConf?.referenceAppointment &&
              event.data.widgetConf?.referenceAppointment?.uuid
            ) {
              LoggerService.warn('Received reference appointment UUID from post message');
              this.swRouteService.setReferenceAppointmentUuidFromIframePostMessage(event.data.widgetConf.referenceAppointment.uuid);
            }
            if (event.data.widgetConf.widgetContext) {
              // create custom field values data
              this.swRouteService.setWidgetContextDataFromIframePostMessage(event.data.widgetConf.widgetContext);
            }
            this.getWidgetConfiguration(event.data.widgetConf.partner_uuid, event.data.widgetConf.widget_uuid);
          }
        }

        if (event.data.eventName === WINDOW_LISTENER_CONSTANTS.LANGUAGE_CHANGED_EVENT) {
          if (event.data.language) {
            this.translate.use(event.data.language);
            this.translate.setFallbackLang(event.data.language);
          }
        }

        if (event.data.eventName === WINDOW_LISTENER_CONSTANTS.APPOINTMENT_BOOKING_DONE) {
          const appointmentBookings: CalendarAppointmentModel[] = event.data.bookingData;
          this.customEventService.appointmentBookingDoneEvent.emit({
            bookingData: appointmentBookings
          });
        }
      }
    });

    // Initialize parent URL detection for Stripe redirects
    this.swRouteService.initParentUrl();
  }

  getAppointment(appointmentUuid: string): void {
    this.appointmentService.get(appointmentUuid).subscribe({
      next: (appointment) => {
        if (appointment?.id) {
          this.swRouteService.setReferenceAppointment = appointment;
        }
      },
      error: () => LoggerService.warn('Ignoring appointment as invalid appointment uuid is passed', this.swRouteService.getReferenceAppointmentUuid)
    });
  }

  setWidgetConfigurationFromQueryParams(partner_uuid: string, widget_uuid: string): void {
    this.widgetService.layoutType = 'IFRAME_URL';
    // this.widgetService.layoutType = 'CHATBOX';
    this.getWidgetConfiguration(partner_uuid, widget_uuid);
  }

  /**
   * Activate a translation language for error states shown before (or instead of)
   * the widget config loading. Reads the URL locale/lang param and falls back to
   * the brand default, so the blocked-partner message renders translated text
   * rather than raw i18n keys.
   */
  private initLanguageForErrorState(): void {
    const params = new URLSearchParams(window.location.search);
    // Normalize via resolveLocale so dash-form / family-code params (fr-CH, fr)
    // map to a real translation key (fr_CH); a raw param would miss the exact-key
    // TRANSLATION_MAP lookup and fall back to German.
    const raw = params.get('locale') || params.get('lang');
    const lang = this.helperService.resolveLocale(raw) || environment.defaultLocale;
    this.translate.use(lang);
    this.translate.setFallbackLang(lang);
  }

  private loadIframeResizerJs(version: string) {
    // In WC mode the widget is not inside an iframe; injecting iframe-resizer into the host
    // page is meaningless and potentially harmful.
    if (this.widgetService.isWebComponentMode) { return; }
    if (!this.iframeResizerLoaded) {
      this.helperService.loadIframeResizerJs(version);
      this.iframeResizerLoaded = true;
    }
  }

  getWidgetConfiguration(partnerUUID: string, widgetUUID: string): void {
    this.widgetService.partnerUUID = partnerUUID;
    this.widgetService.widgetUUID = widgetUUID;

    // A re-fetch (a reset, or a BOOT post-message pointing at a DIFFERENT
    // partner/widget) must start from a clean slate: otherwise a stale widgetConf
    // keeps the previous partner's booking UI on screen (the first template
    // branch matches on the old `bookingwidget_enabled === 1`), and a stale error
    // flag from a prior load lingers. Re-arm the loading skeleton too, so a
    // re-fetch shows the skeleton (as the first load does) instead of an empty
    // window where the widget — trigger bubble included — briefly disappears.
    // removeSmartWidgetLoadingClass() clears it again on every outcome.
    this.widgetService.widgetConf = undefined;
    this.isPartnerBlocked = false;
    this.isBookingWidgetDisabled = false;
    this.hasBootstrapError = false;
    this.isSmartWidgetLoading = true;

    this.widgetService.getWidgetConfiguration(partnerUUID, widgetUUID)
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe({
        next: widgetConf => {
          this.loadIframeResizerJs(widgetConf?.partner?.iframe_resizer_version);
          if (!widgetConf) {
            LoggerService.warn('Widget Configuration is not available');
            return;
          }

          if (this.swRouteService.zipCodeFromQueryParamsData) {
            const zip = {
              value: this.swRouteService.zipCodeFromQueryParamsData,
              operator: "=",
            };
            this.bookingService.getStores({ zip })
              .pipe(
                takeUntilDestroyed(this.destroyRef),
                finalize(() => this.removeSmartWidgetLoadingClass())
              )
              .subscribe({
                next: stores => {
                  if (stores?.length) {
                    widgetConf.context.store_id = stores[0].id;
                    widgetConf.context.store = stores[0];
                  } else {
                    LoggerService.warn('Stores are not available for this zip code', this.swRouteService.zipCodeFromQueryParamsData);
                  }
                  this.updateWidgetConfiguration(this.swRouteService.overrideWidgetConfigDataFromQueryParamData(widgetConf));
                },
                error: () => this.updateWidgetConfiguration(this.swRouteService.overrideWidgetConfigDataFromQueryParamData(widgetConf))
              });
          } else if (this.swRouteService.storeIdFromQueryParamsData) {
            this.bookingService.getStoreByIdOrUUid(this.swRouteService.storeIdFromQueryParamsData)
              .pipe(
                takeUntilDestroyed(this.destroyRef),
                finalize(() => this.removeSmartWidgetLoadingClass())
              )
              .subscribe({
                next: store => {
                  if (store) {
                    widgetConf.context.store_id = store.id;
                    widgetConf.context.store = store;
                  } else {
                    LoggerService.warn('Store is not available for this store id', this.swRouteService.storeIdFromQueryParamsData);
                  }
                  this.updateWidgetConfiguration(this.swRouteService.overrideWidgetConfigDataFromQueryParamData(widgetConf));
                },
                error: () => this.updateWidgetConfiguration(this.swRouteService.overrideWidgetConfigDataFromQueryParamData(widgetConf))
              });
          } else {
            this.updateWidgetConfiguration(this.swRouteService.overrideWidgetConfigDataFromQueryParamData(widgetConf));
            this.removeSmartWidgetLoadingClass();
          }
        },
        error: (error: HttpErrorResponse) => {
          // A blocked/deleted partner (partner_blocked) or a partner whose booking
          // widget is switched off (booking_widget_disabled, JB-1637) makes the
          // configuration call return 403, so widgetConf is never assigned and the
          // bookingwidget_enabled branches below can't fire. Without this the widget
          // renders blank.
          // The language is normally set from the loaded config (setupWidgetLang),
          // which never runs on any error path — so translate has no active
          // language and the message would render as raw i18n keys. Initialise it
          // from the URL locale here, exactly as the classic widget does.
          this.initLanguageForErrorState();

          const errorCode: string | undefined = error?.status === HttpStatusCode.Forbidden
            ? error?.error?.errors?.[0]?.error_code
            : undefined;
          if (errorCode === SMART_WIDGET_ERROR_CODES.PARTNER_BLOCKED) {
            this.isPartnerBlocked = true;
          } else if (errorCode === SMART_WIDGET_ERROR_CODES.BOOKING_WIDGET_DISABLED) {
            this.isBookingWidgetDisabled = true;
          } else {
            // Any other failure (unknown 403 code, 5xx, network) — show the generic
            // "unavailable" card rather than leaving the widget blank (no branch
            // would otherwise match once widgetConf is never assigned).
            this.hasBootstrapError = true;
          }

          this.removeSmartWidgetLoadingClass();
        }
      });
  }

  private updateWidgetConfiguration(widgetConf: WidgetConfModel): void {
    // Before the INIT_CONFIG postMessage below: it structured-clones widgetConf at send time and the
    // host page keeps that copy (widgetConf.dbConfig in assets/smart-widget/main.js), so resolving
    // any later would leave the "browser" sentinel on the host's copy permanently. setupWidgetLang()
    // still calls this — the early return makes the second call a no-op.
    this.resolveBrowserLocaleSentinel(widgetConf);

    if (this.widgetService.clientOriginEvent) {
      this.widgetService.sendMessageToMainJs(this.widgetService.clientOriginEvent, {
        eventName: WINDOW_LISTENER_CONSTANTS.INIT_CONFIG,
        widgetConf: widgetConf
      });
    }
    this.widgetService.widgetConf = widgetConf;
    // The context twin of the `refresh` query param. This is the earliest point the context
    // exists, and it is still before any booking component can mount (they are template-gated
    // on widgetConf), so the clear cannot race a booking-state read. Must stay AFTER the
    // assignment above and BEFORE the setup calls below.
    this.swRouteService.applyContextRefresh(widgetConf?.context);
    this.cssOverwriteService.checkForCssTemplateOrActiveCss();
    this.setupWidgetLang(widgetConf);
    this.setupWidgetWorker(widgetConf);
  }

  setupWidgetLang(widgetConf: WidgetConfModel): void {
    // Must run FIRST: everything below (and every later reader of widgetConf.locale) must see a
    // real locale rather than the "browser" sentinel.
    this.resolveBrowserLocaleSentinel(widgetConf);

    // Pre-select the service mapped to the resolved locale when the language→service map is
    // enabled. This only maps the service — the UI language stays URL/config-driven via the
    // block below, which always runs.
    this.applyLanguageServiceMapping(widgetConf);

    const lang = widgetConf?.context?.lang || widgetConf?.lang || widgetConf?.context?.locale || widgetConf?.locale;
    if (lang) {
      this.translate.use(lang);
      this.translate.setFallbackLang(lang);
    }
  }

  /**
   * THE single resolution point for the "Browser language" locale option (JB-1634): replaces the
   * BROWSER_LOCALE_SENTINEL on widgetConf.locale with the visitor's actual language, in place.
   *
   * The write-back is the whole design. widgetConf.locale is read in more places than the UI
   * language — booking-summary-view.component.ts copies it into the booking payload's
   * user_browser_language / user_selected_language — so resolving it here, once, at config load is
   * what keeps the literal string "browser" off appointment records without touching those call
   * sites. Resolve-at-read would need every consumer to know about the sentinel.
   *
   * Deliberately runs even when a ?lang=/?locale= URL param is present: the param already wins the
   * UI language through the precedence chain in setupWidgetLang(), but the sentinel still has to be
   * cleared off the config object so it cannot leak from the lower-precedence fallbacks.
   */
  private resolveBrowserLocaleSentinel(widgetConf: WidgetConfModel): void {
    if (widgetConf?.locale !== BROWSER_LOCALE_SENTINEL) {
      return;
    }

    widgetConf.locale = this.helperService.resolveAutoLocale(
      widgetConf.supported_widget_languages,
      widgetConf.partner?.language_identifier
    );
    LoggerService.log(`[browser-language] sentinel resolved to ${widgetConf.locale}`);
  }

  /**
   * Pre-selects the service mapped to the resolved locale so the existing skip path drops the
   * service step. Locale precedence: browser language → URL lang/locale → first map key (each
   * only if its language is present in the map). No-op — and the service step shows — when the
   * feature is off, the map is empty, a service is already pre-selected (explicit selection
   * wins), or no mapped locale resolves.
   *
   * Maps the SERVICE only; it never changes the UI language — that stays URL/config-driven in
   * setupWidgetLang().
   *
   * The map is keyed by language family ("de", "en", "fr") — region-agnostic, so every German
   * visitor (de_CH/de_DE/de_AT) and every English visitor (en_US/en_GB/en_AU/en_CA) lands on the
   * same entry. A full-locale key ("de_CH", "en_US") is still honoured for older configs.
   */
  private applyLanguageServiceMapping(widgetConf: WidgetConfModel): void {
    const context = widgetConf?.context;
    const map = context?.language_service_map;
    const hasMap = !!map && Object.keys(map).length > 0;

    if (!context?.enable_language_service_map || !hasMap) {
      if (context?.enable_language_service_map && !hasMap) {
        LoggerService.warn('[language-service-map] enabled but the map is empty; falling back to default language handling');
      }
      return;
    }

    // Explicit pre-selection (query param / config) wins — leave language + service untouched.
    if (context.appointment_service_ids?.length > 0) {
      LoggerService.warn(`[language-service-map] service already pre-selected (${context.appointment_service_ids}); skipping mapping`);
      return;
    }

    LoggerService.log('[language-service-map] enabled; resolving locale. map:', map);

    // Look up a full locale ("de_CH") by its language family ("de") first — the canonical,
    // region-agnostic key — then by the full locale itself for backward compatibility.
    const mapServiceId = (locale: string | undefined): number | undefined => {
      if (!locale) {
        return undefined;
      }
      const family = this.helperService.languageOf(locale);
      const value = family != null && map[family] != null ? map[family] : map[locale];
      return value == null ? undefined : Number(value);
    };

    const browserLocale = this.helperService.getBrowserLocale();
    const urlLocale = this.helperService.resolveLocale(context.lang || context.locale);
    LoggerService.log(`[language-service-map] candidate locales — browser: ${browserLocale || 'n/a'}, url(lang/locale): ${urlLocale || 'n/a'}`);

    let resolvedLocale: string | undefined;
    if (mapServiceId(browserLocale) != null) {
      resolvedLocale = browserLocale;
      LoggerService.log(`[language-service-map] resolved locale from browser language: ${resolvedLocale}`);
    } else if (mapServiceId(urlLocale) != null) {
      resolvedLocale = urlLocale;
      LoggerService.log(`[language-service-map] browser language not mapped; resolved from URL lang/locale: ${resolvedLocale}`);
    } else {
      // First configured key may be a family ("de") or a full locale ("de_CH"); normalise either
      // to a full Calenso locale so the service-map lookup resolves consistently.
      resolvedLocale = this.helperService.resolveLocale(Object.keys(map)[0]);
      LoggerService.log(`[language-service-map] browser and URL locales not mapped; using first configured key: ${resolvedLocale || 'n/a'}`);
    }

    if (!resolvedLocale) {
      LoggerService.warn('[language-service-map] no supported locale resolved from the map; showing the service step');
      return;
    }

    const serviceId = mapServiceId(resolvedLocale);
    if (serviceId != null && serviceId > 0) {
      LoggerService.log(`[language-service-map] auto-selecting service ${serviceId} for locale ${resolvedLocale}; skipping the service step`);
      context.appointment_service_ids = [serviceId];
    } else {
      LoggerService.warn(`[language-service-map] no valid service mapped for locale ${resolvedLocale}; showing the service step`);
    }
  }

  setupWidgetWorker(widgetConf: WidgetConfModel): void {
    if (widgetConf?.context?.worker_uuid) {
      this.widgetService.getWidgetWorker(widgetConf.context?.worker_uuid).subscribe(
        (result: WorkerDbModel) => {
          if (result) {
            this.widgetService.widgetWorkerData = result;
          } else {
            console.warn('Widget worker is not available');
          }
        }
      );
    }
  }

  // Registered from bundled markup, not fetched. MatIconRegistry loads through HttpClient, so
  // an icon URL is CORS-gated AND picks up the bearer AuthTokenInterceptor adds to every
  // request - which makes it a non-simple request, and the static origins answer the preflight
  // with `Access-Control-Allow-Headers: Content-Type`. As a web component (running on the
  // integrator's origin) every icon therefore failed with status 0, silently, because
  // sentry.config.ts drops that status. Inlining removes the request entirely: no preflight, no
  // dependency on per-vhost .htaccess or CloudFront header policy, same behaviour everywhere.
  addSvgIcon(): void {
    MAT_ICON_LITERALS.forEach(icon => {
      this.iconRegistry.addSvgIconLiteral(
        icon.iconName,
        this.sanitizer.bypassSecurityTrustHtml(icon.svg)
      );
    });
  }

  private removeSmartWidgetLoadingClass(): void {
    setTimeout(() => {
      this.isSmartWidgetLoading = false;
    }, 1000 * 2);
  }
}
